import React from 'react';
import { Grid2, useTheme } from '@mui/material';
import { Functions, Inventory2Outlined } from '@mui/icons-material';
import { CHART_VIEWS } from './ItemAnalytics';
import MetricCard from './MetricCard';
import { countPeriodsInRange } from './useChartData';

function MetricCardList({ summaryData, chartView, dateRange, period }) {
    const theme = useTheme();
    
    if (!summaryData) return null;

    const numDays = dateRange && dateRange.startDate && dateRange.endDate
        ? Math.ceil((new Date(dateRange.endDate) - new Date(dateRange.startDate)) / (1000 * 60 * 60 * 24)) + 1
        : 0;
    const numPeriods = countPeriodsInRange(dateRange?.startDate, dateRange?.endDate, period);

    const averageUsed = summaryData.used_quantity?.day_level?.avg || 0;
    const maximumUsed = summaryData.used_quantity?.day_level?.max || 0;
    const averageTrashed = summaryData.trashed_quantity?.day_level?.avg;
    const totalTrashed = summaryData.trashed_quantity?.total;
    const averageRun = summaryData.run_quantity?.day_level?.avg;
    const totalRun = summaryData.run_quantity?.total;
    const averageRestock = summaryData.restock_quantity?.day_level?.avg;
    const totalRestock = summaryData.restock_quantity?.total;

    return (
        <Grid2 container spacing={2} sx={{ alignItems: 'stretch' }}>
            {chartView === CHART_VIEWS.INVENTORY_VIEW && (
                <>
                    <MetricCard
                        header='Totals'
                        subheader='Totals over the selected date range'
                        gridSize={4}
                        values={[
                            {
                                label: 'Cases',
                                value: summaryData?.total_cases || 0,
                                color: 'info.main',
                                valueType: 'quantity'
                            },
                            {
                                label: 'Used Quantity',
                                value: summaryData?.used_quantity?.total || 0,
                                color: 'success.main',
                                valueType: 'quantity'
                            },
                        ]}
                    />
                    <MetricCard
                        header='Daily Usage'
                        subheader={`Used quantity per day over ${numDays} days`}
                        gridSize={4}
                        values={[
                            {
                                label: 'Median',
                                value: summaryData.used_quantity?.day_level?.median || 0,
                                color: 'primary.main',
                                valueType: 'quantity'
                            },
                            {
                                label: 'Maximum',
                                value: summaryData.used_quantity?.day_level?.max || 0,
                                color: 'primary.main',
                                valueType: 'quantity'
                            }
                        ]}
                    />
                    <MetricCard
                        header='Usage Statistics per Case'
                        subheader={`Used quantity per case over ${summaryData.total_cases} cases`}
                        gridSize={4}
                        values={[
                            {
                                label: 'Median',
                                value: summaryData.used_quantity?.case_level?.median || 0,
                                color: 'info.main',
                                valueType: 'quantity'
                            },
                            {
                                label: 'Maximum',
                                value: summaryData.used_quantity?.case_level?.max || 0,
                                color: 'info.main',
                                valueType: 'quantity'
                            }
                        ]}
                        toggle={{
                            values: [
                                {
                                    label: `${period?.charAt(0).toUpperCase() + period?.slice(1)}ly Average`,
                                    value: (averageUsed.toFixed(0) || 0)
                                },
                                {
                                    label: `${period?.charAt(0).toUpperCase() + period?.slice(1)}ly Maximum`,
                                    value: (maximumUsed || 0)
                                },
                            ],
                            altHeader: `Usage Statistics per ${period?.charAt(0).toUpperCase() + period?.slice(1)}`,
                            subheader: `Used quantity over ${numPeriods} ${period}s`,
                            defaultState: false,
                            leftIcon: <Inventory2Outlined />,
                            rightIcon: <Functions />,
                            tooltip: 'Switch between selected range and daily case level data'
                        }}
                    />
                </>
            )}
            {chartView === CHART_VIEWS.PICKLIST_VIEW && (
                <>
                    <MetricCard
                        header='Totals'
                        subheader='Totals over the selected date range'
                        gridSize={3}
                        values={[
                            {
                                label: 'Used Quantity',
                                value: summaryData?.used_quantity?.total || 0,
                                color: 'success.main',
                                valueType: 'quantity'
                            },
                            {
                                label: 'Picked Quantity',
                                value: summaryData?.open_quantity?.total + summaryData?.hold_quantity?.total || 0,
                                color: 'success.main',
                                valueType: 'quantity'
                            },
                            {
                                label: 'Cases',
                                value: summaryData?.total_cases || 0,
                                color: 'info.main',
                                valueType: 'quantity'
                            },
                        ]}
                    />
                    <MetricCard
                        header='Run'
                        subheader={`Run quantity - median and maximum values across all ${summaryData.total_cases} cases`}
                        gridSize={3}
                        values={[
                            {
                                label: 'Median',
                                value: summaryData.run_quantity?.case_level?.median || 0,
                                color: theme.palette.analytics.cardTypes.run || 'primary.main',
                                valueType: 'quantity'
                            },
                            {
                                label: 'Maximum',
                                value: summaryData.run_quantity?.case_level?.max || 0,
                                color: theme.palette.analytics.cardTypes.run || 'primary.main',
                                valueType: 'quantity'
                            },
                            {
                                label: '% of cases with run',
                                value: (summaryData.run_quantity?.case_level?.num_cases_greater_than_zero || 0) / (summaryData.total_cases || 1),
                                color: theme.palette.analytics.cardTypes.run || 'primary.main',
                                valueType: 'percent'
                            }
                        ]}
                        toggle={{
                            values: [
                                {
                                    label: `${period?.charAt(0).toUpperCase() + period?.slice(1)}ly Average`,
                                    value: (averageRun.toFixed(0) || 0)
                                },
                                {
                                    label: 'Total',
                                    value: (totalRun || 0)
                                },
                                {} // Keep percentage unchanged
                            ],
                            subheader: `Run quantity over ${numPeriods} ${period}s`,
                            defaultState: false,
                            leftIcon: <Inventory2Outlined />,
                            rightIcon: <Functions />,
                            tooltip: 'Switch between selected range and daily case level data'
                        }}
                    />
                    <MetricCard
                        header='Restock'
                        subheader={`Restock quantity - median and maximum values across all ${summaryData.total_cases} cases`}
                        gridSize={3}
                        values={[
                            {
                                label: 'Median',
                                value: summaryData.restock_quantity?.case_level?.median || 0,
                                color: theme.palette.analytics.cardTypes.restock || 'success.main',
                                valueType: 'quantity'
                            },
                            {
                                label: 'Maximum',
                                value: summaryData.restock_quantity?.case_level?.max || 0,
                                color: theme.palette.analytics.cardTypes.restock || 'success.main',
                                valueType: 'quantity'
                            },
                            {
                                label: '% of cases with restock',
                                value: (summaryData.restock_quantity?.case_level?.num_cases_greater_than_zero || 0) / (summaryData.total_cases || 1),
                                color: theme.palette.analytics.cardTypes.restock || 'success.main',
                                valueType: 'percent'
                            }
                        ]}
                        toggle={{
                            values: [
                                {
                                    label: `${period?.charAt(0).toUpperCase() + period?.slice(1)}ly Average`,
                                    value: (averageRestock.toFixed(0) || 0)
                                },
                                {
                                    label: 'Total',
                                    value: (totalRestock || 0)
                                },
                                {} // Keep percentage unchanged
                            ],
                            subheader: `Restock quantity over ${numPeriods} ${period}s`,
                            defaultState: false,
                            leftIcon: <Inventory2Outlined />,
                            rightIcon: <Functions />,
                            tooltip: 'Switch between selected range and daily case level data'
                        }}
                    />
                    <MetricCard
                        header='Not Used'
                        subheader={`Open, unused quantity - median and maximum values across all ${summaryData.total_cases} cases`}
                        gridSize={3}
                        values={[
                            {
                                label: 'Median',
                                value: summaryData.trashed_quantity?.case_level?.median || 0,
                                color: theme.palette.analytics.cardTypes.openNotUsed || 'error.main',
                                valueType: 'quantity'
                            },
                            {
                                label: 'Maximum',
                                value: summaryData.trashed_quantity?.case_level?.max || 0,
                                color: theme.palette.analytics.cardTypes.openNotUsed || 'error.main',
                                valueType: 'quantity'
                            },
                            {
                                label: '% of cases with items opened but not used',
                                value: (summaryData.trashed_quantity?.case_level?.num_cases_greater_than_zero || 0) / (summaryData.total_cases || 1),
                                color: theme.palette.analytics.cardTypes.openNotUsed || 'error.main',
                                valueType: 'percent'
                            }
                        ]}
                        toggle={{
                            values: [
                                {
                                    label: `${period?.charAt(0).toUpperCase() + period?.slice(1)}ly Average`,
                                    value: (averageTrashed.toFixed(0) || 0)
                                },
                                {
                                    label: 'Total',
                                    value: (totalTrashed || 0)
                                },
                                {} // Keep percentage unchanged
                            ],
                            subheader: `Open, unused quantity over ${numPeriods} ${period}s`,
                            defaultState: false,
                            leftIcon: <Inventory2Outlined />,
                            rightIcon: <Functions />,
                            tooltip: 'Switch between selected range and daily case level data'
                        }}
                    />
                </>
            )}
        </Grid2>
    );
}

export default MetricCardList;